import React, { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, MessageCircle, Clock, TrendingUp, Send } from "lucide-react";
import { useTranslation } from "react-i18next";

interface Mood {
    emoji: string;
    label: string;
    color: string;
    value: number;
}

const MoodCheckinWidget: React.FC = () => {
    const { t, i18n } = useTranslation("nonHealth");
    const [currentTime, setCurrentTime] = useState(new Date());

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 60000);
        return () => clearInterval(timer);
    }, []);

    const moods: Mood[] = [
        { emoji: "😄", label: t("widgets.moodCheckin.excellent"), color: "from-green-400 to-emerald-500", value: 5 },
        { emoji: "😊", label: t("widgets.moodCheckin.good"), color: "from-lime-400 to-green-400", value: 4 },
        { emoji: "😐", label: t("widgets.moodCheckin.normal"), color: "from-yellow-400 to-amber-400", value: 3 },
        { emoji: "😔", label: t("widgets.moodCheckin.sad"), color: "from-orange-400 to-amber-500", value: 2 },
        { emoji: "😢", label: t("widgets.moodCheckin.bad"), color: "from-red-400 to-rose-500", value: 1 },
    ];

    const [selected, setSelected] = useState<Mood | null>(moods[1]);
    const [submitted, setSubmitted] = useState(false);

    const handleSelect = (mood: Mood) => {
        setSelected(mood);
        setSubmitted(false);
    };

    const handleSubmit = () => {
        if (selected) setSubmitted(true);
    };

    // Generate last 7 days dynamically based on current language
    const days = useMemo(() => {
        const daysList = [];
        const lang = i18n.language || 'fa-IR';
        for (let i = 6; i >= 0; i--) {
            const d = new Date();
            d.setDate(d.getDate() - i);
            daysList.push(
                new Intl.DateTimeFormat(lang, { weekday: 'narrow' }).format(d)
            );
        }
        return daysList;
    }, [i18n.language]);

    // Generate random mock data for the week to simulate dynamic content
    const weekData = useMemo(() => {
        return Array.from({ length: 7 }, () => Math.floor(Math.random() * 4) + 2); // Values between 2 and 5
    }, []);

    return (
        <motion.div
            key="mood-checkin-widget"
            className="relative w-full max-w-sm"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
        >
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-pink-200 relative overflow-hidden">
                {/* background blobs */}
                <motion.div
                    className="absolute -top-10 -right-10 w-28 h-28 bg-pink-300/20 rounded-full blur-2xl"
                    animate={{ scale: [1, 1.3, 1], opacity: [0.4, 0.6, 0.4] }}
                    transition={{ duration: 6, repeat: Infinity }}
                />

                {/* header */}
                <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-2">
                        <motion.div
                            className="w-8 h-8 bg-pink-100 rounded-full flex items-center justify-center"
                            animate={{ scale: [1, 1.1, 1] }}
                            transition={{ duration: 2, repeat: Infinity, repeatType: "mirror" }}
                        >
                            <Heart className="w-4 h-4 text-pink-600" />
                        </motion.div>
                        <h5 className="text-pink-800 font-semibold text-base">{t("widgets.moodCheckin.title")}</h5>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-400">
                        <Clock className="w-3 h-3" />
                        <span>{currentTime.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit', hour12: false })}</span>
                    </div>
                </div>

                {/* mood selector */}
                <div className="flex justify-between gap-2 mb-4">
                    {moods.map((mood) => (
                        <motion.button
                            key={mood.value}
                            className={`flex-1 flex flex-col items-center gap-1 p-2 rounded-xl border-2 transition-all ${
                                selected?.value === mood.value ? "border-pink-400 bg-pink-50" : "border-gray-100 bg-white/50 hover:border-pink-200"
                            }`}
                            whileHover={{ scale: 1.08, y: -3 }}
                            whileTap={{ scale: 0.94 }}
                            onClick={() => handleSelect(mood)}
                        >
                            <motion.span className="text-2xl" animate={selected?.value === mood.value ? { scale: [1, 1.3, 1] } : {}} transition={{ duration: 0.5 }}>
                                {mood.emoji}
                            </motion.span>
                            <span className={`text-[10px] font-medium ${selected?.value === mood.value ? "text-pink-700" : "text-gray-400"}`}>{mood.label}</span>
                        </motion.button>
                    ))}
                </div>

                {/* selected mood display */}
                <AnimatePresence mode="wait">
                    {selected && (
                        <motion.div
                            key={selected.value}
                            className={`bg-gradient-to-r ${selected.color} p-3 rounded-xl mb-3 text-white flex items-center gap-3`}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ duration: 0.3 }}
                        >
                            <motion.span className="text-3xl" animate={{ rotate: [0, 10, -10, 0] }} transition={{ duration: 2, repeat: Infinity }}>
                                {selected.emoji}
                            </motion.span>
                            <div>
                                <p className="font-bold">{t("widgets.moodCheckin.status", { mood: selected.label })}</p>
                                <p className="text-xs opacity-80">{t("widgets.moodCheckin.careMessage")}</p>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* message input */}
                <div className="bg-white/70 rounded-lg border border-pink-100 p-2.5 mb-3 flex items-center gap-2">
                    <MessageCircle className="w-4 h-4 text-pink-400 flex-shrink-0" />
                    <input type="text" placeholder={t("widgets.moodCheckin.placeholder")} className="flex-1 bg-transparent text-sm text-gray-600 placeholder-gray-300 outline-none text-right" dir="rtl" />
                </div>

                {/* weekly chart */}
                <div className="bg-pink-50 rounded-xl p-3 mb-3 border border-pink-100">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-pink-700 text-xs font-medium flex items-center gap-1">
                            <TrendingUp className="w-3.5 h-3.5" />
                            {t("widgets.moodCheckin.weeklyTrend")}
                        </span>
                        <span className="text-xs text-gray-400">{t("widgets.moodCheckin.last7Days")}</span>
                    </div>
                    <div className="flex items-end gap-1 h-10">
                        {days.map((day, i) => (
                            <div key={i} className="flex-1 flex flex-col items-center gap-1">
                                <motion.div
                                    className="w-full rounded-t-sm bg-gradient-to-t from-pink-400 to-rose-300"
                                    initial={{ height: 0 }}
                                    animate={{ height: `${(weekData[i] / 5) * 100}%` }}
                                    transition={{ duration: 0.6, delay: i * 0.1, ease: "backOut" }}
                                />
                                <span className="text-[9px] text-gray-400">{day}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* submit button */}
                <AnimatePresence mode="wait">
                    {!submitted ? (
                        <motion.button
                            key="submit-btn"
                            className="w-full bg-gradient-to-r from-pink-400 to-rose-500 text-white py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                            whileHover={{ scale: 1.02, y: -2, boxShadow: "0 5px 15px rgba(244,114,182,0.4)" }}
                            whileTap={{ scale: 0.98 }}
                            onClick={handleSubmit}
                        >
                            <Send className="w-4 h-4" />
                            {t("widgets.moodCheckin.submit")}
                        </motion.button>
                    ) : (
                        <motion.div
                            key="success-msg"
                            className="w-full bg-green-50 border border-green-200 text-green-700 py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <motion.span animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 0.5 }}>
                                ✅
                            </motion.span>
                            {t("widgets.moodCheckin.successMessage")}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </motion.div>
    );
};

export default MoodCheckinWidget;